import { UserAccessPanelPolicy } from '@helios/state-management'

export enum PanelCategory {
  //The essential tools for making adjustments
  ESSENTIALS = 'Essentials',

  //Panels to monitor and ensure data delivery from start to finish
  DELIVERY = 'Delivery',

  //Tools that enable configuration of accounts'
  CONFIGURATION = 'Configuration',

  //Additional detail, research and tool panels to enable decision making
  SUPPLEMENTARY = 'Supplementary',

  //Tools for taking action across many accounts
  BULK_TOOLS = 'Bulk Tools',

  //Panels that are designed to help the user use and understand Helios (Getting Started)
  HELP = 'Help',
}

/** Interface for all panels within the Helios UI. */
export declare interface PanelMetadata {
  /**
   * This key needs to be unique for your panel.
   * This key is also used to identify the panel for the layout.
   * Because of this, once this key is defined it should never change.
   */
  key: string
  /** The name of the panel. */
  title: string
  /** A brief description of the panel. */
  description: string
  /** Used for categorizing apps in the panel catalog. */
  category: PanelCategory
  /**
   * The permission required to access this panel.
   * I purposely set the type to | undefined as opposed to accessPermission?: ... in order to require the developer
   * to explicitly set it in order to prevent accidentally not requiring a permission.
   */
  accessPermission: keyof UserAccessPanelPolicy | undefined
  /**
   * Whether the panel is external client facing.
   * Setting this to true will display the panel in the panel catalog regardless of user access.
   * It will be disabled and show a tooltip to request access if user does not have access.
   */
  isExternal?: boolean
  /**
   * Whether the user has access to this panel.
   */
  hasAccess?: boolean
  /**
   * Whether to withhold the panel from production realms (prd1/prd2), while leaving it available
   * on alpha, beta and stg so it can be built and tested. Independent of `accessPermission`, which
   * gates on who the user is rather than where the app is running.
   *
   * Filtering happens in `getAvailablePanels`, which feeds both the panel catalog and the layout
   * manager — so a withheld panel is absent from the catalog and cannot be restored from a saved
   * layout either.
   */
  hideInProduction?: boolean
}
